import type { ChatForm } from './flow';

/*
 * The illustration beside each chat page (public/test-prep/img), drawn for who the learner is: young
 * children on the elementary pages, middle schoolers, teens on the SAT/ACT pages, college students,
 * and adults on the grad-school and professional exam pages. Until the page knows the learner's age
 * (a general subject page before the grade question), the art shows learners of every age, or a parent
 * and child once the visitor says it's for their child.
 */

export type Audience = 'general' | 'family' | 'kids' | 'tween' | 'teen' | 'college' | 'adult';

/** The art for the first question's page and its roadmap page, by audience. */
type Scene = 'learner' | 'sell' | 'grade' | 'when' | 'zip' | 'district' | 'partner' | 'learning';

/** The teen set is the original SAT/ACT art; the others live in img/art/. */
const TEEN: Record<Scene, string> = {
  learner: 'chat-learner.webp',
  sell: 'sell-learner.webp',
  grade: 'chat-grade.webp',
  when: 'chat-when.webp',
  zip: 'chat-zip.webp',
  district: 'chat-district.webp',
  partner: 'chat-top-tutor.webp',
  learning: 'chat-grade.webp',
};

const set = (audience: Exclude<Audience, 'teen'>, scenes: ReadonlyArray<Scene>, fallback: Partial<Record<Scene, string>> = {}) =>
  Object.fromEntries(
    (Object.keys(TEEN) as Scene[]).map((scene) => [scene, scenes.includes(scene) ? `art/${audience}-${scene}.webp` : fallback[scene] ?? TEEN[scene]]),
  ) as Record<Scene, string>;

const ART: Record<Audience, Record<Scene, string>> = {
  teen: TEEN,
  kids: set('kids', ['learner', 'sell', 'when', 'zip', 'district', 'partner', 'learning'], { grade: 'art/family-grade.webp' }),
  tween: set('tween', ['learner', 'sell', 'when', 'zip', 'district', 'partner', 'learning'], { grade: 'art/family-grade.webp' }),
  college: set('college', ['learner', 'sell', 'when', 'zip']),
  adult: set('adult', ['learner', 'sell', 'when', 'zip']),
  family: set('family', ['grade', 'sell'], { learner: 'art/kids-learner.webp', when: 'art/kids-when.webp', zip: 'art/kids-zip.webp' }),
  general: set('general', [], { learner: 'art/gen-learner.webp', grade: 'art/gen-grade.webp', sell: 'art/gen-sell.webp' }),
};

/** The legacy grade answers, as audiences ("Other" is the professional pages' assumed grade). */
const GRADE_AUDIENCE: Record<string, Audience> = {
  'Elementary school': 'kids',
  'Middle school': 'tween',
  'High School': 'teen',
  'College or Graduate': 'college',
  'Adult Learner': 'adult',
  Other: 'adult',
};

/** The exam timing question on the grad-school and professional pages, drawn for the exam. */
const EXAM_ART: ReadonlyArray<[RegExp, string]> = [
  [/^lsat|^bar|law/, 'art/exam-lsat.webp'],
  [/^(mcat|dat|oat|pcat|nclex|usmle)|nurs|medic/, 'art/exam-mcat.webp'],
  [/^gmat/, 'art/exam-gmat.webp'],
  [/^gre/, 'art/exam-gre.webp'],
  [/^(cpa|cma|cfa|ea)|account/, 'art/exam-cpa.webp'],
  [/^(series|sie\b)|finra|insurance|real_estate/, 'art/exam-finance.webp'],
  [/^pmp|capm|project|scrum|itil/, 'art/exam-pmp.webp'],
];

type Answers = Readonly<Record<string, string>>;

/** Who the art should show, from what the page and the answers so far say about the learner. */
export function audienceFor(form: ChatForm, answers: Answers): Audience {
  const grade = answers['grade-level'] ?? answers['grade-level-hs-col'] ?? form.assumedGradeLevel ?? undefined;
  if (grade && GRADE_AUDIENCE[grade]) return GRADE_AUDIENCE[grade];
  // The grad-school and professional exam pages ask when the exam is instead of the grade.
  if (form.steps.some((step) => step.key === 'when-taking')) return 'adult';
  // Young children's subjects that still ask the grade (kindergarten, phonics).
  const subject = (form.subject ?? '').toLowerCase();
  if (/kindergarten|elementary|phonics|preschool|pre_k|kids/.test(subject)) return 'kids';
  if (/middle_school/.test(subject)) return 'tween';
  // Pages whose grade question is high school or college only (physics, calculus, ADHD).
  const gradeStep = form.steps.find((step) => step.key === 'grade-level' || step.key === 'grade-level-hs-col');
  if (gradeStep && !gradeStep.options?.some((option) => option.value === 'Elementary school')) return 'teen';
  if (!gradeStep) return 'teen';
  return answers['who-needs-help'] === 'My child' ? 'family' : 'general';
}

const SCENE_BY_KEY: Record<string, Scene> = {
  'who-needs-help': 'learner',
  'grade-level': 'grade',
  'grade-level-hs-col': 'grade',
  how_soon: 'when',
  'when-taking': 'when',
  zipcode: 'zip',
  'school-district': 'district',
  'partner-school': 'partner',
  'learning-differences': 'learning',
};

/** Art drawn for one question, whoever the learner is. */
const KEY_ART: Record<string, string> = {
  'sat-section': 'art/teen-sat-section.webp',
  'adult-learning-goal': 'art/adult-goal.webp',
  'taken-before': 'art/adult-taken-before.webp',
};

/**
 * The answers given before a step (and the step's own, with `inclusive`), so a page keeps the art it
 * was shown with when a later answer changes the audience.
 */
const answersUpTo = (form: ChatForm, key: string, answers: Answers, inclusive: boolean): Answers => {
  const index = form.steps.findIndex((step) => step.key === key);
  if (index < 0) return answers;
  const keys = new Set(form.steps.slice(0, inclusive ? index + 1 : index).map((step) => step.key));
  return Object.fromEntries(Object.entries(answers).filter(([answerKey]) => keys.has(answerKey)));
};

/** The art for a question's page (a file in public/test-prep/img). */
export function artForStep(step: { key: string }, form: ChatForm, answers: Answers): string {
  if (KEY_ART[step.key]) return KEY_ART[step.key];
  const audience = audienceFor(form, answersUpTo(form, step.key, answers, false));
  // The exam pages' timing question (and "How soon…" on the professional pages) shows the exam.
  if ((step.key === 'when-taking' || (step.key === 'how_soon' && audience === 'adult')) && form.subject) {
    const subject = form.subject.toLowerCase();
    const exam = EXAM_ART.find(([pattern]) => pattern.test(subject));
    if (exam) return exam[1];
  }
  return ART[audience][SCENE_BY_KEY[step.key] ?? 'grade'];
}

/** The art for the roadmap page after a step's answer (the first question's). */
export function sellArt(step: { key: string }, form: ChatForm, answers: Answers): string {
  return ART[audienceFor(form, answersUpTo(form, step.key, answers, true))].sell;
}

/** Every art file the chat can show, for the review page and tests. */
export const ALL_ART: ReadonlyArray<string> = Array.from(
  new Set([...Object.values(ART).flatMap((scenes) => Object.values(scenes)), ...Object.values(KEY_ART), ...EXAM_ART.map(([, file]) => file)]),
);
